import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { api } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { t } from "@/lib/strings";
import type { MailContent } from "@/lib/types";

const MAIL_RE = /\.(eml|msg)$/i;

// In-app document viewer: a dimmed overlay (portaled to body). For PDFs it hosts
// the file in an <iframe> (the browser's built-in viewer, opening IN Flow). For
// .eml/.msg mail files it fetches the parsed content and renders headers + the
// body inside a locked-down sandboxed iframe. Click the backdrop or Esc to close;
// a "Herunterladen" fallback stays available.
export function FileViewer({
  url,
  name,
  onClose,
}: {
  url: string;
  name: string;
  onClose: () => void;
}) {
  // Derive the render mode from the URL PATH extension, not the link label —
  // mirrors MarkdownContent's routing so a mislabeled link can't push a file
  // into the wrong (unsandboxed, for PDFs) branch. Single source of truth.
  const isMail = MAIL_RE.test(url.split(/[?#]/)[0] ?? "");
  const [mail, setMail] = useState<MailContent | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(isMail);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    if (!isMail) return;
    let cancelled = false;
    // The mail endpoint keys on the storage key (the part after /api/files/).
    const key = url.replace(/^.*\/api\/files\//, "");
    setLoading(true);
    setError(false);
    api
      .getMailContent(key)
      .then((m) => {
        if (!cancelled) {
          setMail(m);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isMail, url]);

  return createPortal(
    <div role="dialog" aria-modal="true" onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={panel}>
        <div style={header}>
          <span style={title} title={name}>
            {name}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <a href={url} download style={downloadLink}>
              {t.fileDownload}
            </a>
            <button type="button" onClick={onClose} title={t.closeEsc} style={closeBtn}>
              <Icon name="x" size={18} color="var(--ink-1)" />
            </button>
          </div>
        </div>
        {isMail ? (
          <MailView name={name} mail={mail} loading={loading} error={error} />
        ) : (
          <iframe src={url} title={name} style={frame} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function MailView({
  name,
  mail,
  loading,
  error,
}: {
  name: string;
  mail: MailContent | null;
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <div style={mailCentered}>{t.mailLoading}</div>;
  if (error || !mail) return <div style={mailCentered}>{t.mailLoadError}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={mailHeaderCard}>
        {mail.subject ? <div style={mailSubject}>{mail.subject}</div> : null}
        <MetaRow label={t.mailFrom} value={mail.from} />
        <MetaRow label={t.mailTo} value={mail.to} />
        <MetaRow label={t.mailCc} value={mail.cc} />
        <MetaRow label={t.mailDate} value={mail.date} />
        {mail.attachments.length > 0 ? (
          <div style={attachRow}>
            <span style={mailMetaLabel}>{t.mailAttachments}</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {mail.attachments.map((a, i) => (
                <span key={i} style={attachChip} title={`${a.filename} (${a.content_type})`}>
                  <Icon name="file" size={12} color="var(--ink-2)" />
                  {a.filename}
                  <span style={{ color: "var(--ink-3)" }}>{fmtBytes(a.size)}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {mail.has_remote_content ? (
          <div style={blockedNote}>
            <Icon name="globe" size={12} color="var(--ink-3)" />
            {t.mailRemoteBlocked}
          </div>
        ) : null}
      </div>
      {/* Untrusted mail HTML: no allow-scripts / no allow-same-origin + a deny-all
          CSP (only data: images render). The browser sandbox is the XSS boundary. */}
      <iframe title={name} sandbox="" srcDoc={buildSrcDoc(mail)} style={mailFrame} />
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div style={mailMetaRow}>
      <span style={mailMetaLabel}>{label}</span>
      <span style={{ minWidth: 0, wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function buildSrcDoc(mail: MailContent): string {
  const csp = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";
  const inner =
    mail.body_html ??
    `<pre style="white-space:pre-wrap;margin:0;font:inherit">${escapeHtml(mail.body_text ?? "")}</pre>`;
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${csp}"></head>` +
    `<body style="margin:0;padding:14px;font:14px/1.55 system-ui,-apple-system,sans-serif;` +
    `color:#111;background:#fff;word-break:break-word">${inner}</body></html>`
  );
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0,0,0,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "3vh 3vw",
};

const panel: CSSProperties = {
  width: "min(1100px, 94vw)",
  height: "94vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--surface)",
  borderRadius: 10,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "10px 14px",
  borderBottom: "1px solid var(--hairline)",
  background: "var(--surface-elev)",
};

const title: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: "var(--ink-1)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const downloadLink: CSSProperties = {
  color: "var(--ink-2)",
  fontSize: 12.5,
  fontWeight: 500,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const closeBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
};

const frame: CSSProperties = {
  flex: 1,
  width: "100%",
  border: "none",
  background: "var(--surface-soft)",
};

const mailFrame: CSSProperties = {
  flex: 1,
  width: "100%",
  border: "none",
  background: "#fff",
};

const mailCentered: CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-3)",
  fontSize: 13,
};

const mailHeaderCard: CSSProperties = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--hairline)",
  background: "var(--surface-elev)",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  maxHeight: "34vh",
  overflowY: "auto",
};

const mailSubject: CSSProperties = {
  fontSize: 15,
  fontWeight: 650,
  color: "var(--ink-1)",
  marginBottom: 4,
  wordBreak: "break-word",
};

const mailMetaRow: CSSProperties = {
  display: "flex",
  gap: 8,
  fontSize: 12.5,
  color: "var(--ink-1)",
  lineHeight: 1.5,
};

const mailMetaLabel: CSSProperties = {
  flex: "0 0 52px",
  color: "var(--ink-3)",
  fontWeight: 500,
};

const attachRow: CSSProperties = {
  display: "flex",
  gap: 8,
  fontSize: 12.5,
  marginTop: 4,
  alignItems: "flex-start",
};

const attachChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 8px",
  borderRadius: 6,
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  fontSize: 12,
  color: "var(--ink-1)",
};

const blockedNote: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 6,
  fontSize: 12,
  color: "var(--ink-2)",
};
